import React from 'react';
import { getPointsTextColorClass } from '../../../Utils/ColorUtils';
import ReadOnlyCodeViewer from '../../Utils/ReadOnlyCodeViewer';
import FollowupQuestionsAccordion from './Shards/FollowupQuestionsAccordion';
import AudioPlayer from '../../../Utils/AudioPlayer';
import VideoPlayer from '../../../Utils/VideoPlayer';

/**
 * ChallengeAnswerCard Component
 * Displays the answer section for a challenge question with score and reason
 * 
 * @param {Object} question - The question object
 * @param {boolean} showAnswers - Whether to show the answer section
 */
const ChallengeAnswerCard = ({ question, showAnswers = false }) => {
  
  if (!showAnswers) return null;

  // Define code question types
  const codeQuestionTypes = ['code-write', 'code-completion', 'code-explanation', 'code-refactoring', 'find-the-bug', 'security-task', 'performance-optimization'];

  // Render answer section
  const renderAnswer = () => {
    // For empty answers, show a message
    if (!question.answer) {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <p className="text-gray-500 italic">No answer provided</p>
        </div>
      );
    }
    
    // Don't show placeholder answers
    if (question.answer === "[USER ANSWER APPENDED HERE]") return null;
    
    // Handle audio-answer type - primarily audio with optional text transcript
    if (question.type === 'audio-answer') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          {question.answer && typeof question.answer === 'string' && (
            <p className="text-gray-800 bg-blue-50 p-3 rounded-md mb-2 whitespace-pre-wrap">
              {question.answer}
            </p>
          )}
          <AudioPlayer file_key={question.file_key} />
        </div>
      );
    }
    
    // Handle video-answer type - primarily video with optional text transcript
    if (question.type === 'video-answer') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          {question.answer && typeof question.answer === 'string' && (
            <p className="text-gray-800 bg-blue-50 p-3 rounded-md mb-2 whitespace-pre-wrap">
              {question.answer}
            </p>
          )}
          <VideoPlayer file_key={question.file_key} />
        </div>
      );
    }
    
    // For multiple-answer questions, the answer might be an array
    if (question.type === 'multiple-answer' && Array.isArray(question.answer)) {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <div className="flex flex-row flex-wrap gap-2">
            {question.answer.map((ans, index) => (
              <div key={index} className="px-4 py-2 bg-blue-100 rounded-full text-blue-700">
                {ans}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // For single-answer questions
    if (question.type === 'single-answer' && typeof question.answer === 'string') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
            {question.answer}
          </div>
        </div>
      );
    }
    
    // For true-false questions
    if (question.type === 'true-false' && typeof question.answer === 'string') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
            {question.answer}
          </div>
        </div>
      );
    }
    
    // Handle explanation-type questions separately - they should be a textarea, not a codebox
    if (question.type === 'code-explanation' || question.type === 'find-the-bug' || 
        question.type === 'security-task' || question.type === 'performance-optimization') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <p className="text-gray-800 bg-blue-50 p-3 rounded-md whitespace-pre-wrap">
            {question.answer}
          </p>
          <AudioPlayer file_key={question.file_key} />
        </div>
      );
    }
    
    // For long-text and short-text questions
    if (['long-text', 'short-text'].includes(question.type) && typeof question.answer === 'string') {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <p className="text-gray-800 bg-blue-50 p-3 rounded-md">
            {question.answer}
          </p>
          <AudioPlayer file_key={question.file_key} />
        </div>
      );
    }
    
    // Check for other code questions BEFORE checking for string answers
    // This includes both questions with code types and questions with codeSnippet property
    if (codeQuestionTypes.includes(question.type) || question.codeSnippet) {
      return (
        <div className="mt-4 border-t pt-3 border-gray-200">
          <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
          <ReadOnlyCodeViewer 
            code={question.answer || ''}
            skillsTested={question.skillsTested}
            maxHeight={400}
          />
        </div>
      );
    }
    
    // Fallback for any other type of answer
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
        <p className="text-gray-800">
          {typeof question.answer === 'object' && question.answer !== null
            ? (question.answer.text 
                ? question.answer.text 
                : JSON.stringify(question.answer))
            : (question.answer !== null && question.answer !== undefined
                ? question.answer.toString()
                : 'No answer provided')}
        </p>
      </div>
    );
  };

  // Render score and reason section (unique to challenges)
  const renderScoreAndReason = () => {
    return (
      <div className="mt-4 border-t pt-3 border-gray-200">
        <div className="grid grid-cols-8 gap-4">
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-2">Points:</h4>
            <p className={`text-2xl font-medium ${getPointsTextColorClass(question.points)}`}>
              {question.points !== undefined && question.points !== null 
                ? `${question.points}/5` 
                : <span className="text-gray-500 italic text-base">None</span>}
            </p>
          </div>
          <div className="col-span-7">
            <h4 className="text-md font-medium text-gray-700 mb-2">Reason:</h4>
            <p className="text-gray-800">
              {question.reason 
                ? question.reason 
                : <span className="text-gray-500 italic">No reason provided</span>}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderAnswer()}
      <FollowupQuestionsAccordion 
        followupQuestions={question.followup_questions_and_answers || []} 
        skillsTested={question.skillsTested}
      />
      {renderScoreAndReason()}
    </>
  );
};

export default ChallengeAnswerCard;
